import { Link, useRouterState } from '@tanstack/react-router';
import { Icon, IconName, Typography } from '@/primer/components';

interface NavTab {
  path: string;
  label: string;
  icon: IconName;
  activeIcon: IconName;
}

const NAV_TABS: NavTab[] = [
  {
    path: '/dashboard',
    label: 'Home',
    icon: 'home',
    activeIcon: 'home',
  },
  {
    path: '/support',
    label: 'Support',
    icon: 'circle-question-mark',
    activeIcon: 'circle-question-mark',
  },
  {
    path: '/settings',
    label: 'Settings',
    icon: 'cog',
    activeIcon: 'cog',
  },
];

/**
 * BottomTabBar — mobile-only bottom navigation bar.
 *
 * Rendered as a fixed bar at the bottom of the viewport.
 * Hidden on tablet and above (md:+); visible on mobile (< 800px).
 *
 * Active tab shows the icon inside a `bg-interactive-quiet` pill.
 * Active route detection is path-prefix based.
 */
export function BottomTabBar() {
  const routerState = useRouterState();
  const currentPath = routerState.location.pathname;

  return (
    <nav
      className="flex md:hidden fixed bottom-0 left-0 right-0 z-50 bg-surface border-t border-default h-[var(--bottom-tab-bar-height)]"
      aria-label="Main navigation"
      data-testid="bottom-tab-bar"
    >
      {NAV_TABS.map((tab) => {
        const isActive = currentPath === tab.path || currentPath.startsWith(`${tab.path}/`);

        return (
          <Link
            key={tab.path}
            to={tab.path}
            aria-current={isActive ? 'page' : undefined}
            className="flex flex-1 flex-col items-center py-3 gap-1 no-underline"
          >
            <div
              className={`flex items-center justify-center px-4 py-1 rounded-full transition-colors`}
            >
              <Icon
                name={isActive ? tab.activeIcon : tab.icon}
                className={isActive ? 'icon-interactive' : 'icon-default'}
              />
            </div>
            <Typography
              variant="body"
              size="small"
              color={isActive ? 'interactive' : 'bodylighter'}
              as="span"
            >
              {tab.label}
            </Typography>
          </Link>
        );
      })}
    </nav>
  );
}
